import React, { Fragment, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router';
import qs from 'query-string';

import CardElementPlaceholder from '../CardElementPlaceholder';
import CardElementAddressControl from './CardElementAddressControl';
import UserAddressDisplay from '../../../../components/global/user-address/UserAddressDisplay';
import { openModal } from '../../../../actions/modal-actions';
import { PHONE_AUTH_MODAL } from '../../../../constants/modals';
import { isCardElementEditable } from '../AccountCard/helpers';
import useVirtualPageView from '../../../../apps/pro/services/analytics/useVirtualPageView';
import { postNativeAppCardsCardElementOpen } from '../../../native-bridge/native-apps-interface';
import useAccountCardNativeFeatureSupport from '../../hooks/useAccountCardNativeFeatureSupport';
import Button from '../../../../components/global/Button';
import AccountCardAddressBookTable from '../../views/AccountCardConfigurator/components/AccountCardAddressBookTable/modal';
import { BASENAME } from '../../../../constants/basenames';
import useBasename from '../../../../components/routing/useBasename';
import { mapRosterIdentitiesToCustomPrintRecipients } from '../../../../apps/pro/views/AccountAddressBook/components/AccountAddressBookTable/helpers';
import AddressIcon from '../../../../components/icons/AddressIcon';

const propTypes = {
  element: PropTypes.shape({
    cardElement: PropTypes.object.isRequired,
    cardElementConfiguration: PropTypes.shape({
      external_shipping_address: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        address_line_1: PropTypes.string,
        address_line_2: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        post_code: PropTypes.string,
        country: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
      }),
      roster_identity_linked_identity_addresses: PropTypes.shape({
        total: PropTypes.number,
        records: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string.isRequired,
          }),
        ),
      }),
      use_family_name: PropTypes.bool,
    }).isRequired,
    loading: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  accountCard: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  viewOnly: PropTypes.bool,
  setSelectedRosterIdentityIds: PropTypes.func,
};

const CardElementAddress = ({
  element,
  onChange,
  accountCard,
  viewOnly,
  setSelectedRosterIdentityIds,
}) => {
  // Check to open the spreadsheet importer immediately
  const location = useLocation();
  const basename = useBasename();
  const defaultImportOpen = !!qs.parse(location.search)?.import;

  const { nativeSupportsCardElementConfiguration } =
    useAccountCardNativeFeatureSupport();

  const [editing, setEditing] = useState(defaultImportOpen);
  const { cardElementConfiguration } = element;
  const {
    external_shipping_addresses: externalShippingAddresses,
    roster_identity_linked_identity_addresses:
      rosterIdentityLinkedIdentityAddresses,
  } = cardElementConfiguration;
  const externalShippingAddressesLength =
    externalShippingAddresses?.records?.length;
  const rosterIdentityAddressesLength =
    rosterIdentityLinkedIdentityAddresses?.records?.length;

  const { onVirtualPageViewTrack } = useVirtualPageView({ fireOnMount: false });

  const dispatch = useDispatch();
  const onLogin = useCallback(
    onSuccess =>
      dispatch(
        openModal(PHONE_AUTH_MODAL, {
          onLogin: onSuccess,
          introText: (
            <Fragment>
              Verify your mobile number to sign in and{' '}
              <strong>send this card to your favorite people!</strong> Waldo
              will text a verification code to the number below.
            </Fragment>
          ),
        }),
      ),
    [],
  );
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const isEditable = isCardElementEditable(element);

  return (
    <Fragment>
      <a
        href="#"
        className={classNames({
          'card-element card-element-address': true,
          'card-element-address-has-contacts':
            externalShippingAddressesLength || rosterIdentityAddressesLength,
          'card-element-loading': element.loading,
        })}
        onClick={event => {
          event.preventDefault();

          if (!isEditable) {
            return;
          }

          if (
            nativeSupportsCardElementConfiguration(element.cardElement.type)
          ) {
            postNativeAppCardsCardElementOpen({
              accountCardId: accountCard.id,
              cardElementId: element.cardElement.id,
              cardElementType: element.cardElement.type,
              cardElementConfiguration: cardElementConfiguration.id
                ? cardElementConfiguration
                : null,
            });

            return;
          }

          if (!authenticated) {
            onVirtualPageViewTrack({
              pageViewTitle: 'Create a card - Log in',
              pageViewPath: '/auth',
            });

            onLogin(() => setEditing(true));
            return;
          }

          setEditing(true);
        }}
      >
        {externalShippingAddressesLength > 0 ||
        rosterIdentityAddressesLength > 0 ? (
          <div className="card-element-address-display">
            {externalShippingAddressesLength === 1 ||
            rosterIdentityAddressesLength === 1 ? (
              <UserAddressDisplay
                address={
                  externalShippingAddresses?.records?.[0] ||
                  rosterIdentityLinkedIdentityAddresses?.records?.[0]
                }
                familyName={cardElementConfiguration.use_family_name}
              />
            ) : (
              <div className="card-element-address-display-multiple-notice">
                <p>
                  Delivered to multiple addresses (
                  {externalShippingAddressesLength ||
                    rosterIdentityAddressesLength}
                  ).
                </p>
                {viewOnly ? (
                  <p className="text-italic mb-0">Press to view</p>
                ) : (
                  <p className="text-italic mb-0">
                    Press to review &amp; edit.
                  </p>
                )}
              </div>
            )}
            <p className="text-primary mb-0 mt-1 text-extra-bold text-xs">
              Edit Recipients
            </p>
          </div>
        ) : (
          <CardElementPlaceholder
            icon={viewOnly ? <AddressIcon color="#0d84fe" /> : undefined}
          >
            {!viewOnly && (
              <Button onClick={e => e.preventDefault()}>Add recipients</Button>
            )}
          </CardElementPlaceholder>
        )}
      </a>

      <CardElementAddressControl
        // Handling external shipping addresses.
        // Only open when Roster Identities are not involved.
        forceOpen={editing && basename === BASENAME.SHUTTERBUG}
        onClose={() => setEditing(false)}
        onChange={input => {
          onChange(element, {
            external_shipping_address_ids: input.addresses.map(({ id }) => id),
            use_family_name:
              input.familyName ?? cardElementConfiguration.use_family_name,
          });
        }}
        addresses={externalShippingAddresses?.records}
        accountCard={accountCard}
        defaultFamilyName={cardElementConfiguration?.use_family_name}
        defaultImportOpen={defaultImportOpen}
        contentClassName={classNames({
          'card-element-address-control-view-only': viewOnly,
        })}
      />

      <AccountCardAddressBookTable
        forceOpen={editing && basename !== BASENAME.SHUTTERBUG}
        onClose={() => setEditing(false)}
        addresses={rosterIdentityLinkedIdentityAddresses?.records ?? []}
        defaultFamilyName={cardElementConfiguration?.use_family_name}
        onAddRecipients={(selectedRosterIdentities, useFamilyName) => {
          const rosterIdentityLinkedIdentities =
            mapRosterIdentitiesToCustomPrintRecipients(
              selectedRosterIdentities,
            );
          // Set the selected roster identities
          setSelectedRosterIdentityIds?.(
            rosterIdentityLinkedIdentities.map(
              ({ rosterIdentityId }) => rosterIdentityId,
            ),
          );
          const addressesIds = rosterIdentityLinkedIdentities
            .map(({ address }) => address?.id)
            .filter(Boolean);

          onChange(element, {
            roster_identity_linked_identity_address_ids: addressesIds,
            use_family_name: !!useFamilyName,
          });
          setEditing(false);
        }}
      />
    </Fragment>
  );
};

CardElementAddress.propTypes = propTypes;

export default CardElementAddress;
